import React, { useState, useEffect } from 'react';
import ProtectedContent from './ProtectedContent';

/**
 * The 78 approved "Thought for the day" entries (Tudor review, 9 Oct 2026).
 *
 *   • One thought a day, same list for the dashboard and the games.
 *   • Anchor date is 7 Oct 2026 — that day shows thought 1.
 *   • Index = whole days since anchor, modulo 78, using the user's local date.
 *   • Text is used exactly as approved and must not be edited without a
 *     SuperAdmin update.
 *
 * SuperAdmin editability is deferred to the SuperAdmin "Thoughts" tab (a
 * follow-up task); the list is intentionally held here as the single source
 * of truth until that panel is built.
 */
const ARP_THOUGHTS = [
  "Recovery is built one ordinary day at a time.",
  "You don't have to feel ready to take the next right step.",
  "A craving is a wave. It rises, it peaks and it passes.",
  "Asking for help is a skill, not a weakness.",
  "Today only asks for today.",
  "Honesty with yourself comes before honesty with anyone else.",
  "Rest is part of Recovery.",
  "Small promises kept rebuild big trust.",
  "Feelings are information. They are not instructions.",
  "Your past explains you. It doesn't define you.",
  "Call someone before you need to.",
  "Clean and sober today is a full achievement.",
  "Progress is quieter than you expect.",
  "A hard day is not a failed day.",
  "Notice what helped today and do more of it.",
  "Being kind to yourself is not the same as letting yourself off.",
  "The urge to drink or drug is not an order.",
  "Hungry, angry, lonely or tired? Deal with that first.",
  "Gratitude is easier to find when you look for small things.",
  "You can start your day again at any hour.",
  "Healing is rarely a straight line.",
  "Say no to one thing today that drains you.",
  "Patience with others starts with patience with yourself.",
  "Every meeting you attend is a vote for your future.",
  "Write down one thing that went right today.",
  "Shame hides. Recovery shows up.",
  "You are allowed to change your mind about who you are.",
  "Good habits are built from boring repetition.",
  "Trust grows back slowly. Keep showing up.",
  "Sleep, food, water and fresh air are Recovery tools too.",
  "Being honest about a relapse is braver than hiding it.",
  "Peace often starts with one less argument.",
  "You don't have to fix everything today.",
  "Listening is a gift you can give anyone.",
  "Look after your body. It carried you this far.",
  "The person you are becoming is worth the effort.",
  "Make amends where you can. Make peace where you can't.",
  "Spend time with people who want you well.",
  "Breathe out slowly. Your body listens.",
  "Change is uncomfortable. That doesn't mean it's wrong.",
  "One honest conversation can change a whole day.",
  "Courage is acting while you're still afraid.",
  "Look for the next right thing, not the perfect thing.",
  "Old places and old people can pull you back. Choose carefully.",
  "Share what's on your mind before it grows.",
  "Every day clean and sober makes the next one a little easier.",
  "Forgiveness is something you do for your own peace.",
  "Your worth isn't measured by your worst day.",
  "Service to others quiets a busy mind.",
  "Learn your warning signs and say them out loud.",
  "Thank someone today who helped you.",
  "Calm is a skill. It gets stronger with practice.",
  "There is no shortcut through Recovery, only a way through.",
  "Pause before you react. The pause is where you choose.",
  "Your story can help someone else one day.",
  "Be curious about your feelings, not afraid of them.",
  "Today, do one thing your future self will thank you for.",
  "Freedom is being able to say no.",
  "Keep your plans simple and your support close.",
  "Humility is seeing yourself clearly, no bigger and no smaller.",
  "Tidy one small space. Order outside helps order inside.",
  "Boredom passes. So do cravings.",
  "Ask yourself what you need, not what you want.",
  "Laughter is allowed in Recovery.",
  "A walk outside can change the way a day feels.",
  "You can't control other people. You can control your next step.",
  "Keep the phone numbers of good people close.",
  "Growth often feels like discomfort at first.",
  "Celebrate the quiet wins nobody else sees.",
  "Respect your limits. They keep you safe.",
  "Hope is a choice you can make again today.",
  "Mistakes become lessons when you're honest about them.",
  "You are not alone, even when it feels that way.",
  "Steady beats fast.",
  "Let today be enough.",
  "Look after your mind the way you'd look after a friend.",
  "Your Recovery belongs to you.",
  "Tomorrow starts with how you finish today."
];

// Anchor: 7 October 2026 shows thought 1 (index 0).
const ANCHOR_YEAR = 2026;
const ANCHOR_MONTH = 10; // 1-indexed
const ANCHOR_DAY = 7;

/** Whole days elapsed between the user's local midnight today and anchor. */
function thoughtIndexForToday() {
  const now = new Date();
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const anchor = new Date(ANCHOR_YEAR, ANCHOR_MONTH - 1, ANCHOR_DAY);
  const diffDays = Math.floor((todayLocal - anchor) / 86400000);
  const n = ARP_THOUGHTS.length;
  return ((diffDays % n) + n) % n;
}

function ThoughtOfTheDay() {
  const [thought, setThought] = useState('');

  useEffect(() => {
    setThought(ARP_THOUGHTS[thoughtIndexForToday()]);
  }, []);

  return (
    <div className="bg-white border-2 border-black rounded-md p-6 mb-6" data-testid="thought-of-the-day" data-arp="thought-of-the-day">
      <h3 className="text-base font-bold text-black mb-2">Thought for the day</h3>
      <ProtectedContent>
        <p className="text-base text-black leading-relaxed italic font-medium">
          "{thought}"
        </p>
      </ProtectedContent>
    </div>
  );
}

export { ARP_THOUGHTS, thoughtIndexForToday };
export default ThoughtOfTheDay;
